
import { defineComponent } from "vue";
import { AxiosResponse } from "axios";
import { iParameter } from "@/components/UI/KnParameterSidebar/KnParameterSidebar";
import { iURLData, iExporter, iSchedulation } from "./DocumentExecution";
import DocumentExecutionBreadcrumb from "./breadcrumbs/DocumentExecutionBreadcrumb.vue";
import DocumentExecutionHelpDialog from "./dialogs/documentExecutionHelpDialog/DocumentExecutionHelpDialog.vue";
import DocumentExecutionRankDialog from "./dialogs/documentExecutionRankDialog/DocumentExecutionRankDialog.vue";
import DocumentExecutionNotesDialog from "./dialogs/documentExecutionNotesDialog/DocumentExecutionNotesDialog.vue";
import DocumentExecutionMetadataDialog from "./dialogs/documentExecutionMetadataDialog/DocumentExecutionMetadataDialog.vue";
import DocumentExecutionMailDialog from "./dialogs/documentExecutionMailDialog/DocumentExecutionMailDialog.vue";
import DocumentExecutionSchedulationsTable from "./tables/documentExecutionSchedulationsTable/DocumentExecutionSchedulationsTable.vue";
import DocumentExecutionLinkDialog from "./dialogs/documentExecutionLinkDialog/DocumentExecutionLinkDialog.vue";
import KnParameterSidebar from "@/components/UI/KnParameterSidebar/KnParameterSidebar.vue";
import { luxonFormatDate } from "@/helpers/commons/localeHelper";
import TieredMenu from "primevue/tieredmenu";
import Registry from "../registry/Registry.vue";
import Dossier from "../dossier/Dossier.vue";
import Olap from "../olap/Olap.vue";
import DocumentExecutionSelectCrossNavigationDialog from "./dialogs/documentExecutionSelectCrossNavigationDialog/DocumentExecutionSelectCrossNavigationDialog.vue";
import DocumentExecutionCNContainerDialog from "./dialogs/documentExecutionCNContainerDialog/DocumentExecutionCNContainerDialog.vue";
import { getCorrectRolesForExecution } from "../../../helpers/commons/roleHelper";
import { findCrossTargetByCrossName, loadNavigationParamsInitialValue } from "./DocumentExecutionCrossNavigationHelper";
import { getValidDate } from "./DocumentExecutionCrossNavigationHelper";
import DatasetEditorPreview from "../dashboard/dataset/DatasetEditorDataTab/DatasetEditorPreview.vue";

const crypto = require("crypto");
const deepcopy = require("deepcopy");
// @ts-ignore
// eslint-disable-next-line
window.execExternalCrossNavigation = function(outputParameters, otherOutputParameters, crossNavigationLabel) {
  postMessage(
    {
      type: "crossNavigation",
      outputParameters: outputParameters,
      inputParameters: {},
      targetCrossNavigation: crossNavigationLabel,
      docLabel: null,
      otherOutputParameters: otherOutputParameters ? [otherOutputParameters] : [],
    },
    "*",
  );
};

// @ts-ignore
// eslint-disable-next-line
window.execPreviewDataset = function(dsLabel, parameters, directDownload) {
  postMessage(
    {
      type: "preview",
      dsLabel: dsLabel,
      parameters: parameters,
      directDownload: directDownload,
    },
    location.origin,
  );
};

export default defineComponent({
  name: "document-execution",
  components: {
    DatasetEditorPreview,
    DocumentExecutionBreadcrumb,
    DocumentExecutionHelpDialog,
    DocumentExecutionRankDialog,
    DocumentExecutionNotesDialog,
    DocumentExecutionMetadataDialog,
    DocumentExecutionMailDialog,
    DocumentExecutionSchedulationsTable,
    DocumentExecutionLinkDialog,
    KnParameterSidebar,
    TieredMenu,
    Registry,
    Dossier,
    Olap,
    DocumentExecutionSelectCrossNavigationDialog,
    DocumentExecutionCNContainerDialog,
  },
  props: {
    id: { type: String },
    parameterValuesMap: { type: Object },
    tabKey: { type: String },
    propMode: { type: String },
    selectedMenuItem: { type: Object },
    menuItemClickedTrigger: { type: Boolean },
  },
  emits: ["close", "updateDocumentName", "parametersChanged"],
  data() {
    return {
      document: null as any,
      hiddenFormData: {} as any,
      hiddenFormUrl: "" as string,
      documentMode: "VIEW",
      filtersData: {} as {
        filterStatus: iParameter[];
        isReadyForExecution: boolean;
      },
      urlData: null as iURLData | null,
      exporters: null as iExporter[] | null,
      mode: null as string | null,
      parameterSidebarVisible: false,
      toolbarMenuItems: [] as any[],
      helpDialogVisible: false,
      documentRank: null as string | null,
      rankDialogVisible: false,
      notesDialogVisible: false,
      metadataDialogVisible: false,
      mailDialogVisible: false,
      metadata: {} as any,
      schedulationsTableVisible: false,
      schedulations: [] as any[],
      linkDialogVisible: false,
      linkInfo: null as {
        isPublic: boolean;
        noPublicRoleError: boolean;
      } | null,
      sbiExecutionId: null as string | null,
      embedHTML: false,
      user: null as any,
      reloadTrigger: false,
      breadcrumbs: [] as any[],
      linkParameters: [],
      embed: false,
      olapCustomViewVisible: false,
      userRole: null,
      loading: false,
      olapDesignerMode: false,
      sessionEnabled: false,
      dateFormat: "" as string,
      destinationSelectDialogVisible: false,
      crossNavigationDocuments: [] as any[],
      angularData: null as any,
      crossNavigationContainerVisible: false,
      crossNavigationContainerData: null as any,
      newCockpitCreated: false,
      loadingCrossNavigationDocument: false,
      crossNavigationSourceDocumentName: "",
      metadataLoading: false,
      datasetPreviewShown: false as boolean,
      datasetToPreview: {} as any,
      reportRefreshInterval: null as any,
    };
  },
  watch: {
    async menuItemClickedTrigger() {
      if (!this.selectedMenuItem) return;
      const routes = ["registry", "document-composite", "report", "office-doc", "olap", "map", "report", "kpi", "dossier", "etl"];
      const test = routes.some((el) => this.selectedMenuItem?.to.includes(el));
      if (!test) return;
      const label = this.selectedMenuItem.to.substring(this.selectedMenuItem.to.lastIndexOf("/") + 1);
      this.document = { label: label };
      if (!this.document.label) return;
      this.breadcrumbs = [];
      this.filtersData = {} as any;
      await this.loadDocument();

      if (this.userRole) {
        await this.loadPage(true);
      } else {
        this.parameterSidebarVisible = true;
      }
    },
  },
  async activated() {
    if (this.mode === "iframe" && this.$route.name !== "new-dashboard") {
      if (this.userRole) {
        await this.loadPage(true);
      } else {
        this.parameterSidebarVisible = true;
      }
    }
  },
  deactivated() {
    this.parameterSidebarVisible = false;
    window.removeEventListener("message", this.iframeEventsListener);
    if (this.reportRefreshInterval) clearInterval(this.reportRefreshInterval);
  },
  computed: {
    canEditCockpit(): boolean {
      if (!this.user || !this.document) return false;
      return (
        (this.document.engine?.toLowerCase() === "knowagecockpitengine" || this.document.engine?.toLowerCase() === "knowagedashboardengine") &&
        (this.user.functionalities?.includes("DocumentAdminManagement") || this.document.creationUser === this.user.userId || (this.document.stateCode === "DEV" && this.user.functionalities?.includes("DocumentDevManagement")))
      );
    },
    sessionRole(): string | null {
      if (!this.user) return null;
      return this.user.sessionRole !== this.$t("role.defaultRolePlaceholder") ? this.user.sessionRole : null;
    },
    url(): string {
      if (this.document) {
        return process.env.VUE_APP_HOST_URL + `/knowage/restful-services/publish?PUBLISHER=documentExecutionNg&OBJECT_ID=${this.document.id}&OBJECT_LABEL=${this.document.label}&TOOLBAR_VISIBLE=false&MENU_PARAMETERS=%7B%7D&LIGHT_NAVIGATOR_DISABLED=TRUE&SBI_EXECUTION_ID=${this.sbiExecutionId}&OBJECT_NAME=${this.document.name}&CROSS_PARAMETER=null`;
      } else {
        return "";
      }
    },
    isParameterSidebarVisible(): boolean {
      if (!this.userRole) return false;

      let parameterVisible = false;
      for (let i = 0; i < this.filtersData?.filterStatus?.length; i++) {
        const tempFilter = this.filtersData.filterStatus[i];
        if (tempFilter.showOnPanel === "true" && tempFilter.visible) {
          parameterVisible = true;
          break;
        }
      }

      return parameterVisible;
    },
    isAndroidDevice() {
      return /Android/i.test(navigator.userAgent);
    },
  },
  async created() {
    window.addEventListener("message", this.iframeEventsListener);

    if (this.propMode !== "document-execution" && !this.$route.path.includes("olap-designer") && this.$route.name !== "document-execution" && this.$route.name !== "document-execution-embed" && this.$route.name !== "document-execution-workspace") return;

    await this.loadUserConfig();

    this.isOlapDesignerMode();
    this.setMode();

    this.document = { label: this.id };

    if (!this.document.label) return;

    await this.loadDocument();

    this.user = (this.$store.state as any).user;
    this.userRole = this.user.sessionRole !== this.$t("role.defaultRolePlaceholder") ? this.user.sessionRole : null;

    let invalidRole = false;
    getCorrectRolesForExecution(this.document).then(async (response: any) => {
      let correctRolesForExecution = response;

      if (!this.userRole) {
        if (correctRolesForExecution.length == 1) {
          this.userRole = correctRolesForExecution[0];
        } else {
          this.parameterSidebarVisible = true;
        }
      } else if (this.userRole) {
        if (correctRolesForExecution.length == 1) {
          let correctRole = correctRolesForExecution[0];
          if (this.userRole !== correctRole) {
            this.$store.commit("setError", {
              title: this.$t("common.error.generic"),
              msg: this.$t("documentExecution.main.userRoleError"),
            });
            invalidRole = true;
          }
        }
      }
      if (!invalidRole) {
        if (this.userRole) {
          await this.loadPage(true);
        } else {
          this.parameterSidebarVisible = true;
        }
      }
    });
  },
  methods: {
    isInDocBrowser() {
      return this.$router.currentRoute.value.matched.some((i) => i.name === "document-browser" || i.name === "document-execution-workspace");
    },
    prepareDriversAndParameter(parameters: any) {
      let tempObj = {} as any;
      if (parameters.length > 0) {
        const tempParams = parameters.map((i) => {
          return {
            name: i.name,
            multiValue: i.multiValue,
            value: i.value,
          };
        });
        tempObj.parameters = tempParams;
      }
      if (this.filtersData?.filterStatus.length > 0) {
        let tempDrivers = {} as any;
        this.filtersData.filterStatus.forEach((i) => {
          tempDrivers[i.urlName] = i.multivalue ? i.parameterValue.map((p) => p.value) : i.parameterValue[0].value;
        });
        tempObj.drivers = tempDrivers;
      }
      return tempObj;
    },
    async directDownloadDataset(dataset: any) {
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/export/dataset/${dataset.id}/csv`, this.prepareDriversAndParameter(dataset.pars), { headers: { Accept: "application/json, text/plain, */*", "Content-Type": "application/json;charset=UTF-8" } })
        .then(() => {
          this.$store.commit("setInfo", {
            title: this.$t("common.toast.updateTitle"),
            msg: this.$t("workspace.myData.exportSuccess"),
          });
        })
        .catch(() => {});
    },
    async iframeEventsListener(event) {
      if (event.data.type === "crossNavigation") {
        await this.executeCrossNavigation(event);
      } else if (event.data.type === "preview") {
        await this.$http
          .get(process.env.VUE_APP_HOST_URL + `/knowage/restful-services/1.0/datasets/${event.data.dsLabel}`)
          .then((response: AxiosResponse<any>) => {
            this.datasetToPreview = response.data[0];
            if (event.data.parameters && event.data.parameters.length > 0) {
              this.datasetToPreview.pars.forEach((i) => {
                if (Object.keys(event.data.parameters[0]).includes(i.name)) {
                  i.value = event.data.parameters[0][i.name];
                }
              });
            }
          })
          .catch(() => {});
        if (event.data.directDownload) this.directDownloadDataset(this.datasetToPreview);
        else this.datasetPreviewShown = true;
      } else if (event.data.type === "cockpitExecuted") {
        this.loading = false;
      }
    },
    editCockpitDocumentConfirm() {
      if (this.documentMode === "EDIT") {
        this.$confirm.require({
          message: this.$t("documentExecution.main.editModeConfirm"),
          header: this.$t("documentExecution.main.editCockpit"),
          icon: "pi pi-exclamation-triangle",
          accept: () => this.editCockpitDocument(),
        });
      } else {
        this.editCockpitDocument();
      }
    },
    async editCockpitDocument() {
      this.loading = true;
      this.documentMode = this.documentMode === "EDIT" ? "VIEW" : "EDIT";
      this.hiddenFormData.set("documentMode", this.documentMode);
      await this.loadURL(null);
    },
    openHelp() {
      this.helpDialogVisible = true;
    },
    async refresh() {
      this.parameterSidebarVisible = false;
      await this.loadURL(null);
      this.reloadTrigger = !this.reloadTrigger;
    },
    toggle(event: Event) {
      this.createMenuItems();
      const menu = this.$refs.menu as any;
      menu.toggle(event);
    },
    createMenuItems() {
      this.toolbarMenuItems = [];

      if (this.exporters && this.exporters.length !== 0) {
        this.toolbarMenuItems.push({
          label: this.$t("common.export"),
          items: [],
        });
      }

      if (this.user.functionalities.includes("EnableToRate") || this.user.functionalities.includes("HelpOnLine")) {
        let tempObj = {
          label: this.$t("common.info.info"),
          items: [] as any,
        };
        if (this.user.functionalities.includes("EnableToRate")) {
          tempObj.items.push({
            icon: "pi pi-star",
            label: this.$t("common.rank"),
            command: () => this.openRank(),
          });
        }
        if (this.user.functionalities.includes("HelpOnLine")) {
          tempObj.items.push({
            icon: "pi pi-book",
            label: this.$t("common.onlineHelp"),
            command: () => this.openHelp(),
          });
        }
        this.toolbarMenuItems.push(tempObj);
      }

      this.toolbarMenuItems.push({
        label: this.$t("common.shortcuts"),
        items: [],
      });

      this.exporters?.forEach((exporter: any) => {
        const exporterindex = this.toolbarMenuItems.findIndex((item: any) => item.label === this.$t("common.export"));
        this.toolbarMenuItems[exporterindex].items.push({
          icon: "fa fa-file-excel",
          label: exporter.name,
          command: () => this.export(exporter.name),
        });
      });

      if (this.user.functionalities.includes("SendMailFunctionality") && this.document.typeCode === "REPORT") {
        const index = this.toolbarMenuItems.findIndex((item: any) => item.label === this.$t("common.info.info"));
        if (index !== -1) {
          this.toolbarMenuItems[index].items.push({
            icon: "pi pi-envelope",
            label: this.$t("common.sendByEmail"),
            command: () => this.openMailDialog(),
          });
        } else {
          this.toolbarMenuItems.push({
            label: this.$t("common.export"),
            items: [
              {
                icon: "pi pi-envelope",
                label: this.$t("common.sendByEmail"),
                command: () => this.openMailDialog(),
              },
            ],
          });
        }
      }

      if (this.user.functionalities.includes("SeeMetadataFunctionality")) {
        const index = this.toolbarMenuItems.findIndex((item: any) => item.label === this.$t("common.info.info"));
        if (index !== -1)
          this.toolbarMenuItems[index].items.unshift({
            icon: "pi pi-info-circle",
            label: this.$t("common.metadata"),
            command: () => this.openMetadata(),
          });
      }

      if (this.user.functionalities.includes("SeeNotesFunctionality")) {
        const index = this.toolbarMenuItems.findIndex((item: any) => item.label === this.$t("common.info.info"));
        if (index !== -1)
          this.toolbarMenuItems[index].items.push({
            icon: "pi pi-file",
            label: this.$t("common.notes"),
            command: () => this.openNotes(),
          });
      }

      if (this.user.functionalities.includes("SeeSnapshotsFunctionality") && this.user.enterprise && this.document.typeCode !== "OLAP") {
        const index = this.toolbarMenuItems.findIndex((item: any) => item.label === this.$t("common.shortcuts"));
        if (index !== -1)
          this.toolbarMenuItems[index].items.unshift({
            icon: "",
            label: this.$t("documentExecution.main.showScheduledExecutions"),
            command: () => this.showScheduledExecutions(),
          });
      }

      if (this.isOrganizerEnabled()) {
        const index = this.toolbarMenuItems.findIndex((item: any) => item.label === this.$t("common.shortcuts"));
        if (index !== -1)
          this.toolbarMenuItems[index].items.unshift({
            icon: "fa fa-suitcase ",
            label: this.$t("documentExecution.main.addToWorkspace"),
            command: () => this.addToWorkspace(),
          });
      }

      if (this.mode === "olap") {
        const index = this.toolbarMenuItems.findIndex((item: any) => item.label === this.$t("common.shortcuts"));
        if (index !== -1)
          this.toolbarMenuItems[index].items.unshift({
            icon: "",
            label: this.$t("documentExecution.main.showOLAPCustomView"),
            command: () => this.showOLAPCustomView(),
          });
      }

      if (this.user.functionalities.includes("EnableToCopyAndEmbed")) {
        const index = this.toolbarMenuItems.findIndex((item: any) => item.label === this.$t("common.shortcuts"));
        if (index !== -1) {
          this.toolbarMenuItems[index].items.push({
            icon: "fa fa-share",
            label: this.$t("documentExecution.main.copyLink"),
            command: () => this.copyLink(false),
          });
          this.toolbarMenuItems[index].items.push({
            icon: "fa fa-share",
            label: this.$t("documentExecution.main.embedInHtml"),
            command: () => this.copyLink(true),
          });
        }
      }

      this.removeEmptyToolbarItems();
    },
    removeEmptyToolbarItems() {
      for (let i = this.toolbarMenuItems.length - 1; i >= 0; i--) {
        if (this.toolbarMenuItems[i].items.length === 0) {
          this.toolbarMenuItems.splice(i, 1);
        }
      }
    },
    export(type: string) {
      if (this.document.typeCode === "OLAP") {
        this.exportOlap(type);
      } else if (this.document.typeCode === "REPORT") {
        let tempUrlData = this.urlData!.url.replace(/([?&]outputType=)[^&]+/, "$1" + type).replace(/([?&]outputType_description=)[^&]+/, "$1" + type);

        window.open(tempUrlData + "&outputType=" + type, "name", "resizable=1,height=750,width=1000");
      } else {
        const filteredFrames = Array.prototype.filter.call(window.frames, (frame) => frame.name);
        const tempIndex = this.breadcrumbs.findIndex((el: any) => el.label === this.document.name);

        let tempFrame = filteredFrames[tempIndex];
        if (tempFrame && tempFrame.name === "documentFrame" + tempIndex) {
          tempFrame.postMessage({ type: "export", format: type.toLowerCase() }, "*");
          return;
        }

        while (tempFrame && tempFrame.name !== "documentFrame" + tempIndex) tempFrame = tempFrame[0].frames;

        tempFrame.postMessage({ type: "export", format: type.toLowerCase() }, "*");
      }
    },
    exportOlap(type: string) {
      const url = type === "PDF" ? `/knowagewhatifengine/restful-services/1.0/model/export/pdf?SBI_EXECUTION_ID=${this.sbiExecutionId}` : `/knowagewhatifengine/restful-services/1.0/model/export/excel?SBI_EXECUTION_ID=${this.sbiExecutionId}`;
      window.open(url);
    },
    openMailDialog() {
      this.mailDialogVisible = true;
    },
    async openMetadata() {
      this.metadataLoading = true;
      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentexecutionee/${this.document.id}/documentMetadata`).then((response: AxiosResponse<any>) => (this.metadata = response.data));
      this.metadataDialogVisible = true;
      this.metadataLoading = false;
    },
    async openRank() {
      await this.getRank();
      this.rankDialogVisible = true;
    },
    openNotes() {
      this.notesDialogVisible = true;
    },
    async showScheduledExecutions() {
      this.loading = true;
      this.parameterSidebarVisible = false;
      this.schedulationsTableVisible = true;
      this.schedulations = [];
      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentsnapshot/getSnapshots?id=${this.document.id}`).then((response: AxiosResponse<any>) => {
        response.data?.schedulers.forEach((el: any) => this.schedulations.push({ ...el, urlPath: response.data.urlPath }));
      });
      this.loading = false;
    },
    async copyLink(embedHTML: boolean) {
      this.loading = true;
      this.linkParameters = this.getFormattedParameters();
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentexecution/canHavePublicExecutionUrl`, { label: this.document.label })
        .then((response: AxiosResponse<any>) => {
          this.embedHTML = embedHTML;
          this.linkInfo = response.data;
          this.linkDialogVisible = true;
        })
        .catch(() => {});
      this.loading = false;
    },
    closeDocumentConfirm() {
      if (this.documentMode === "EDIT") {
        this.$confirm.require({
          message: this.$t("documentExecution.main.closeDocumentConfirmMessage"),
          header: this.$t("documentExecution.main.closeDocumentConfirmTitle"),
          icon: "pi pi-exclamation-triangle",
          accept: () => this.closeDocument(),
        });
      } else {
        this.closeDocument();
      }
    },
    closeDocument() {
      const link = this.$route.path.includes("workspace") ? "/workspace" : "/document-browser";
      this.$router.push(link);
      this.breadcrumbs = [];

      if (this.reportRefreshInterval) clearInterval(this.reportRefreshInterval);

      this.$emit("close");
    },
    setMode() {
      this.embed = this.$route.path.includes("embed");
      if (this.embed) {
        this.$store.commit("setDocumentExecutionEmbed");
      }

      if (this.$route.path.includes("registry")) {
        this.mode = "registry";
      } else if (this.$route.path.includes("dossier")) {
        this.mode = "dossier";
      } else if (this.$route.path.includes("olap")) {
        this.mode = "olap";
      } else {
        this.mode = "iframe";
      }
    },
    async loadPage(initialLoading: boolean = false, documentLabel: string | null = null, crossNavigationPopupMode: boolean = false) {
      this.loading = crossNavigationPopupMode ? false : true;

      await this.loadFilters(initialLoading);
      if (this.filtersData?.isReadyForExecution) {
        this.parameterSidebarVisible = false;
        await this.loadURL(null, documentLabel, crossNavigationPopupMode);
        await this.loadExporters();
      } else if (this.filtersData?.filterStatus) {
        this.parameterSidebarVisible = true;
      }

      this.updateMode();
      this.loading = false;
    },
    updateMode() {
      if (this.document.typeCode === "DATAMART") {
        this.mode = "registry";
      } else if (this.document.typeCode === "DOSSIER") {
        this.mode = "dossier";
      } else if (this.document.typeCode === "OLAP") {
        this.mode = "olap";
      } else {
        this.mode = "iframe";
      }
    },
    async loadDocument() {
      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/documents/${this.document?.label}`).then((response: AxiosResponse<any>) => (this.document = response.data));

      const index = this.breadcrumbs.findIndex((el: any) => el.label === this.document.name);
      if (index !== -1) {
        this.breadcrumbs[index].document = this.document;
      } else {
        this.breadcrumbs.push({
          iframeKey: crypto.randomBytes(16).toString("hex"),
          label: this.document.name,
          document: this.document,
        });
      }
    },
    async loadFilters(initialLoading: boolean = false) {
      if (this.parameterValuesMap && this.parameterValuesMap[this.document.label + "-" + this.tabKey] && initialLoading) {
        this.filtersData = this.parameterValuesMap[this.document.label + "-" + this.tabKey];
        this.setFiltersForBreadcrumbItem();
        return;
      }

      if (this.sessionEnabled && !this.document.navigationParams) {
        const tempFilters = sessionStorage.getItem(this.document.label);
        if (tempFilters) {
          this.filtersData = JSON.parse(tempFilters) as {
            filterStatus: iParameter[];
            isReadyForExecution: boolean;
          };
          this.filtersData.filterStatus?.forEach((filter: any) => {
            if (filter.type === "DATE" && filter.parameterValue[0].value) {
              filter.parameterValue[0].value = new Date(filter.parameterValue[0].value);
            }
          });
          this.setFiltersForBreadcrumbItem();
          return;
        }
      }

      let tempFiltersData: any = null;
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/documentexecution/filters`, {
          label: this.document.label,
          role: this.userRole,
          parameters: this.document.navigationParams ?? {},
        })
        .then((response: AxiosResponse<any>) => (tempFiltersData = response.data))
        .catch((error: any) => {
          if (error.response?.status === 500) {
            this.$store.commit("setError", {
              title: this.$t("common.error.generic"),
              msg: this.$t("documentExecution.main.userRoleError"),
            });
          }
        });

      tempFiltersData?.filterStatus?.forEach((el: iParameter) => {
        el.parameterValue = el.multivalue ? [] : [{ value: "", description: "" }];
        if (el.driverDefaultValue?.length > 0) {
          let valueIndex = "_col0";
          let descriptionIndex = "col1";
          if (el.metadata?.colsMap) {
            valueIndex = Object.keys(el.metadata?.colsMap).find((key: string) => el.metadata.colsMap[key] === el.metadata.valueColumn) as any;
            descriptionIndex = Object.keys(el.metadata?.colsMap).find((key: string) => el.metadata.colsMap[key] === el.metadata.descriptionColumn) as any;
          }

          el.parameterValue = el.driverDefaultValue.map((defaultValue: any) => {
            return {
              value: defaultValue.value ?? defaultValue[valueIndex],
              description: defaultValue.desc ?? defaultValue[descriptionIndex],
            };
          });

          if (el.type === "DATE" && !el.selectionType && el.valueSelection === "man_in" && el.showOnPanel === "true" && el.visible) {
            el.parameterValue[0].value = getValidDate("" + el.parameterValue[0].value, this.dateFormat);
            if (el.driverMaxValue) {
              const validDate = getValidDate("" + el.driverMaxValue, this.dateFormat);
              el.driverMaxDateValue = validDate === "" ? null : validDate;
            }
          }
        }
        if (el.data) {
          el.data = el.data.map((data: any) => {
            return this.formatParameterDataOptions(el, data);
          });

          if (el.data.length === 1) {
            el.parameterValue = [...el.data];
          }
        }
        if ((el.selectionType === "COMBOBOX" || el.selectionType === "LIST") && el.multivalue && el.mandatory && el.data.length === 1) {
          el.showOnPanel = "false";
          el.visible = false;
        }

        if (!el.multivalue && !el.parameterValue) {
          el.parameterValue = [{ value: "", description: "" }];
        }

        if (el.parameterValue[0] && !el.parameterValue[0].description) {
          el.parameterValue[0].description = el.parameterDescription ? el.parameterDescription[0] : "";
        }
      });

      if (tempFiltersData !== null) {
        this.filtersData = tempFiltersData;
      }
      if (this.document.navigationParams) {
        loadNavigationParamsInitialValue(this);
      }

      this.setFiltersForBreadcrumbItem();
    },
    setFiltersForBreadcrumbItem() {
      const index = this.breadcrumbs.findIndex((el: any) => el.label === this.document.name);
      if (index !== -1) this.breadcrumbs[index].filtersData = this.filtersData;
    },
    formatParameterDataOptions(parameter: iParameter, data: any) {
      const valueColumn = parameter.metadata.valueColumn;
      const descriptionColumn = parameter.metadata.descriptionColumn;
      const valueIndex = Object.keys(parameter.metadata.colsMap).find((key: string) => parameter.metadata.colsMap[key] === valueColumn);
      const descriptionIndex = Object.keys(parameter.metadata.colsMap).find((key: string) => parameter.metadata.colsMap[key] === descriptionColumn);

      return {
        value: valueIndex ? data[valueIndex] : "",
        description: descriptionIndex ? data[descriptionIndex] : "",
      };
    },
    async loadURL(olapParameters: any, documentLabel: string | null = null, crossNavigationPopupMode: boolean = false) {
      let error = false;
      const postData = {
        label: this.document.label,
        role: this.userRole,
        parameters: olapParameters ? olapParameters : this.getFormattedParameters(),
        EDIT_MODE: this.documentMode ? this.documentMode : "null",
        IS_FOR_EXPORT: true,
        SBI_EXECUTION_ID: "",
      } as any;

      if (this.sbiExecutionId) {
        postData.SBI_EXECUTION_ID = this.sbiExecutionId;
      }

      if (this.document.typeCode === "MAP") {
        postData.EDIT_MODE = "edit_map";
      }

      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentexecution/url`, postData)
        .then((response: AxiosResponse<any>) => {
          error = false;
          this.urlData = response.data;
          this.sbiExecutionId = this.urlData?.sbiExecutionId as string;
        })
        .catch((response: AxiosResponse<any>) => {
          error = true;
          this.urlData = response.data;
          this.sbiExecutionId = this.urlData?.sbiExecutionId as string;
        });

      if (error) return;

      const index = this.breadcrumbs.findIndex((el: any) => el.label === this.document.name);
      if (index !== -1) {
        this.breadcrumbs[index].urlData = this.urlData;
        this.sbiExecutionId = this.urlData?.sbiExecutionId as string;
      }

      await this.sendForm(documentLabel, crossNavigationPopupMode);

      if (this.document.typeCode === "REPORT" && this.document.refreshSeconds > 0) this.startReportAutoRefresh();
    },
    async loadExporters() {
      if (!this.urlData || !this.urlData.engineLabel) return;
      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/exporters/config/${this.urlData.engineLabel}`).then((response: AxiosResponse<any>) => (this.exporters = response.data.exporters));
    },
    replaceNullForDates(par, value) {
      if (value == "null" && this.filtersData.filterStatus.find((i) => i.urlName === par && i.type === "DATE")) {
        return "";
      } else return value;
    },
    async sendForm(documentLabel: string | null = null, crossNavigationPopupMode: boolean = false) {
      let tempIndex = this.breadcrumbs.findIndex((el: any) => el.label === this.document.name) as any;
      const documentUrl = this.urlData?.url + "&timereloadurl=" + new Date().getTime();
      const postObject = {
        params: { document: null } as any,
        url: documentUrl.split("?")[0],
      };

      if (this.$route.query.documentMode === "edit" && !this.newCockpitCreated) this.documentMode = "EDIT";
      postObject.params.documentMode = this.documentMode;
      this.newCockpitCreated = true;
      this.hiddenFormUrl = postObject.url;
      const paramsFromUrl = documentUrl?.split("?")[1]?.split("&");

      for (let i in paramsFromUrl) {
        if (typeof paramsFromUrl !== "function") {
          postObject.params[paramsFromUrl[i].split("=")[0]] = paramsFromUrl[i].split("=")[1];
        }
      }

      let postForm = document.getElementById("postForm_" + postObject.params.document) as any;
      if (!postForm) {
        postForm = document.createElement("form");
      }
      postForm.id = "postForm_" + postObject.params.document;
      postForm.action = process.env.VUE_APP_HOST_URL + postObject.url;
      postForm.method = "post";
      const iframeName = crossNavigationPopupMode ? "documentFramePopup" : "documentFrame";
      if (this.isAndroidDevice && postObject.params.outputType?.toLowerCase() === "pdf") postForm.target = "_blank";
      else postForm.target = tempIndex !== -1 ? iframeName + tempIndex : documentLabel;
      postForm.acceptCharset = "UTF-8";
      document.body.appendChild(postForm);

      this.hiddenFormData = new URLSearchParams();

      for (let k in postObject.params) {
        const inputElement = document.getElementById("postForm_" + postObject.params.document + k) as any;
        if (inputElement) {
          inputElement.value = decodeURIComponent(postObject.params[k]);
          inputElement.value = inputElement.value.replace(/\+/g, " ");
          inputElement.value = this.replaceNullForDates(k, inputElement.value);

          this.hiddenFormData.set(k, decodeURIComponent(postObject.params[k]).replace(/\+/g, " "));
        } else {
          const element = document.createElement("input");
          element.type = "hidden";
          element.id = "postForm_" + postObject.params.document + k;
          element.name = k;
          element.value = decodeURIComponent(postObject.params[k]);
          element.value = element.value.replace(/\+/g, " ");
          element.value = this.replaceNullForDates(k, element.value);

          postForm.appendChild(element);
          this.hiddenFormData.append(k, decodeURIComponent(postObject.params[k]).replace(/\+/g, " "));
        }
      }

      for (let i = postForm.elements.length - 1; i >= 0; i--) {
        const postFormElement = postForm.elements[i].id.replace("postForm_" + postObject.params.document, "");

        if (!(postFormElement in postObject.params)) {
          postForm.removeChild(postForm.elements[i]);
          this.hiddenFormData.delete(postFormElement);
        }
      }

      this.hiddenFormData.append("documentMode", this.documentMode);

      if (this.document.typeCode === "DATAMART") {
        // let doc = this.document
        // let drivers = doc.drivers
        // let url = `start-qbe?user_id=${this.user?.userUniqueIdentifier}&SBI_EXECUTION_ID=${this.sbiExecutionId}&drivers=${drivers}&registryId=${doc.id}`

        // await this.$http.get(process.env.VUE_APP_QBE_PATH + url)

        await this.sendHiddenFormData();
      } else if (this.document.typeCode === "DOSSIER" || this.document.typeCode === "OLAP") {
        await this.sendHiddenFormData();
      } else {
        postForm.submit();
      }

      const index = this.breadcrumbs.findIndex((el: any) => el.label === this.document.name);
      if (index !== -1) this.breadcrumbs[index].hiddenFormData = this.hiddenFormData;
    },
    async sendHiddenFormData() {
      await this.$http
        .post(this.hiddenFormUrl, this.hiddenFormData, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.9",
          },
        })
        .then(() => {})
        .catch(() => {});
    },
    async onExecute() {
      this.loading = true;
      this.filtersData.isReadyForExecution = true;
      await this.loadURL(null);
      this.parameterSidebarVisible = false;
      this.reloadTrigger = !this.reloadTrigger;

      if (!this.exporters || this.exporters.length === 0) {
        await this.loadExporters();
        const index = this.toolbarMenuItems.findIndex((item: any) => item.label === this.$t("common.export"));
        if (index === -1) {
          this.toolbarMenuItems.splice(1, 0, {
            label: this.$t("common.export"),
            items: [],
          });
        } else {
          this.exporters?.forEach((exporter: any) =>
            this.toolbarMenuItems[index].items.push({
              icon: "fa fa-file-excel",
              label: exporter.name,
              command: () => this.export(exporter.name),
            }),
          );
        }
      }

      if (this.sessionEnabled) {
        this.saveParametersInSession();
      }
      this.loading = false;
    },
    async onExportCSV() {
      const postData = {
        documentId: this.document.id,
        documentLabel: this.document.label,
        exportType: "CSV",
        parameters: this.getFormattedParametersForCSVExport(),
      };
      this.loading = true;
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/export/cockpitData`, postData)
        .then(() => {
          this.$store.commit("setInfo", {
            title: this.$t("common.toast.updateTitle"),
            msg: this.$t("common.exportSuccess"),
          });
        })
        .catch(() => {});
      this.loading = false;
    },
    getFormattedParameters() {
      if (!this.filtersData || !this.filtersData.filterStatus) {
        return {};
      }

      let parameters = {} as any;

      Object.keys(this.filtersData.filterStatus).forEach((key: any) => {
        const parameter = this.filtersData.filterStatus[key];

        if (parameter.parameterValue) {
          if (parameter.type === "DATE" && parameter.parameterValue[0] && parameter.parameterValue[0].value) {
            parameters[parameter.urlName] = this.getFormattedDate(parameter.parameterValue[0].value);
            parameters[parameter.urlName + "_field_visible_description"] = this.getFormattedDate(parameter.parameterValue[0].value, true);
          } else if (parameter.valueSelection === "man_in") {
            if (!parameter.parameterValue[0]) parameter.parameterValue[0] = { value: "", description: "" };
            parameters[parameter.urlName] = parameter.type === "NUM" && parameter.parameterValue[0].value ? +parameter.parameterValue[0].value : parameter.parameterValue[0].value;
            parameters[parameter.urlName + "_field_visible_description"] = parameter.parameterValue[0].value;
          } else if (parameter.selectionType === "TREE" || parameter.selectionType === "LOOKUP" || parameter.multivalue) {
            parameters[parameter.urlName] = parameter.parameterValue.map((el: any) => {
              if (typeof el.value === "object") return el.value[0];
              else return el.value;
            });
            let tempString = "";
            for (let i = 0; i < parameter.parameterValue.length; i++) {
              tempString += parameter.parameterValue[i].description || parameter.parameterDescription?.[i] || "";
              tempString += i === parameter.parameterValue.length - 1 ? "" : ";";
            }
            parameters[parameter.urlName + "_field_visible_description"] = tempString;
          } else {
            parameters[parameter.urlName] = parameter.parameterValue[0] ? parameter.parameterValue[0].value : "";
            parameters[parameter.urlName + "_field_visible_description"] = parameter.parameterValue[0] ? parameter.parameterValue[0].description : "";
          }
        }
      });

      return parameters;
    },
    getFormattedParametersForCSVExport() {
      if (!this.filtersData) return {};
      let parameters = {} as any;

      Object.keys(this.filtersData.filterStatus).forEach((key: any) => {
        const parameter = this.filtersData.filterStatus[key];

        if (parameter.parameterValue) {
          if (parameter.type === "DATE") {
            parameters[parameter.urlName] = this.getFormattedDate(parameter.parameterValue[0].value);
          } else if (parameter.valueSelection === "man_in" && !parameter.multivalue) {
            parameters[parameter.urlName] = parameter.type === "NUM" ? +parameter.parameterValue[0].value : parameter.parameterValue[0].value;
          } else if (parameter.selectionType === "TREE" || parameter.selectionType === "LOOKUP" || parameter.multivalue) {
            let tempString = "";
            for (let i = 0; i < parameter.parameterValue.length; i++) {
              tempString += parameter.parameterValue[i].value;
              tempString += i === parameter.parameterValue.length - 1 ? "" : ",";
            }
            parameters[parameter.urlName] = tempString;
          } else {
            parameters[parameter.urlName] = parameter.parameterValue[0].value;
          }
        }
      });

      return parameters;
    },
    async getRank() {
      this.loading = true;
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `documentrating/getvote`, { obj: this.document.id })
        .then((response: AxiosResponse<any>) => (this.documentRank = response.data))
        .catch((error: any) =>
          this.$store.commit("setError", {
            title: this.$t("common.error.generic"),
            msg: error,
          }),
        );
      this.loading = false;
    },
    async onSaveRank(newRank: any) {
      if (newRank) {
        this.loading = true;
        await this.$http
          .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `documentrating/vote`, { rating: newRank, obj: this.document.id })
          .then(() =>
            this.$store.commit("setInfo", {
              title: this.$t("common.toast.updateTitle"),
              msg: this.$t("documentExecution.main.rankSaveSucces"),
            }),
          )
          .catch((error: any) =>
            this.$store.commit("setError", {
              title: this.$t("common.error.generic"),
              msg: error,
            }),
          );
        this.loading = false;
      }
      this.rankDialogVisible = false;
    },
    async onMetadataSave(metadata: any) {
      this.metadataLoading = true;
      const jsonMeta = [] as any[];
      const properties = ["shortText", "longText", "file"];
      properties.forEach((property: string) =>
        metadata[property].forEach((el: any) => {
          if (el.value || (property === "file" && el.fileToSave)) {
            if (property === "file") el.value = JSON.stringify(el.value);
            jsonMeta.push(el);
          }
        }),
      );

      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentexecutionee/saveDocumentMetadata`, { id: this.document.id, jsonMeta: jsonMeta })
        .then(() => {
          this.$store.commit("setInfo", {
            title: this.$t("common.toast.createTitle"),
            msg: this.$t("common.toast.success"),
          });
          this.metadataDialogVisible = false;
        })
        .catch(() => {});
      this.metadataLoading = false;
    },
    async onMailSave(mail: any) {
      this.loading = true;
      const postData = {
        ...mail,
        label: this.document.label,
        docId: this.document.id,
        userId: this.user.userId,
        parameters: this.getFormattedParameters(),
      };
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentexecutionmail/sendMail`, postData)
        .then(() => {
          this.$store.commit("setInfo", {
            title: this.$t("common.toast.createTitle"),
            msg: this.$t("common.sendMailSuccess"),
          });
          this.mailDialogVisible = false;
        })
        .catch((error: any) => {
          this.$store.commit("setError", {
            title: this.$t("common.error.generic"),
            msg: error,
          });
        });
      this.loading = false;
    },
    async onDeleteSchedulation(schedulation: any) {
      this.loading = true;
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/documentsnapshot/deleteSnapshot`, { SNAPSHOT: "" + schedulation.id })
        .then(async () => {
          this.removeSchedulation(schedulation);
          this.$store.commit("setInfo", {
            title: this.$t("common.toast.deleteTitle"),
            msg: this.$t("common.toast.deleteSuccess"),
          });
        })
        .catch(() => {});
      this.loading = false;
    },
    removeSchedulation(schedulation: iSchedulation) {
      const index = this.schedulations.findIndex((el: any) => el.id === schedulation.id);
      if (index !== -1) this.schedulations.splice(index, 1);
    },
    getFormattedDate(date: any, useDefaultFormat?: boolean) {
      const configurations = (this.$store.state as any).configurations;
      const format = date instanceof Date ? undefined : configurations["SPAGOBI.DATE-FORMAT-SERVER.format"];
      return luxonFormatDate(date, format, useDefaultFormat ? undefined : this.dateFormat);
    },
    async onBreadcrumbClick(item: any) {
      this.document = item.document;
      this.filtersData = item.filtersData;
      this.urlData = item.urlData;
      this.hiddenFormData = item.hiddenFormData;
      this.documentMode = "VIEW";
      this.updateMode();
    },
    async onRoleChange(role: string) {
      this.userRole = role as any;
      this.filtersData = {} as {
        filterStatus: iParameter[];
        isReadyForExecution: boolean;
      };
      this.urlData = null;
      this.exporters = null;
      await this.loadPage();
    },
    async executeCrossNavigation(event: any) {
      this.angularData = event.data;
      await this.loadCrossNavigationByDocument(event.data);
    },
    async loadCrossNavigationByDocument(angularData: any) {
      if (!this.document) return;
      let temp = {} as any;

      this.loadingCrossNavigationDocument = true;
      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/crossNavigation/${this.document.label}/loadCrossNavigationByDocument`).then((response: AxiosResponse<any>) => (temp = response.data));
      this.loadingCrossNavigationDocument = false;

      if (temp.length === 0) return;

      const crossTarget = findCrossTargetByCrossName(angularData, temp);

      if (!crossTarget && temp.length > 1) {
        this.crossNavigationDocuments = temp;
        this.destinationSelectDialogVisible = true;
      } else {
        this.loadCrossNavigation(crossTarget ?? temp[0], angularData);
      }
    },
    async loadCrossNavigation(crossNavigationDocument: any, angularData: any) {
      this.formatAngularOutputParameters(angularData.otherOutputParameters);
      const navigationParams = this.formatNavigationParams(angularData.otherOutputParameters, crossNavigationDocument ? crossNavigationDocument.navigationParams : []);
      this.addDocumentOtherParametersToNavigationParams(navigationParams, angularData, crossNavigationDocument);
      const popupOptions = crossNavigationDocument?.popupOptions ? JSON.parse(crossNavigationDocument.popupOptions) : null;
      this.checkIfParameterHasFixedValue(navigationParams, crossNavigationDocument);

      const sourceDocumentName = this.document.name;
      if (crossNavigationDocument?.crossType !== 2) {
        this.document = {
          ...crossNavigationDocument?.document,
          navigationParams: navigationParams,
        };
      }

      if (crossNavigationDocument?.crossType === 2) {
        this.openCrossNavigationInNewWindow(popupOptions, crossNavigationDocument, navigationParams);
      } else if (crossNavigationDocument?.crossType === 1) {
        const documentLabel = crossNavigationDocument.document.label;
        this.crossNavigationSourceDocumentName = sourceDocumentName;
        this.crossNavigationContainerData = {
          documentLabel: documentLabel,
          documentName: crossNavigationDocument.document.name,
          iFrameName: documentLabel,
        };
        this.crossNavigationContainerVisible = true;
        await this.loadPage(false, documentLabel, true);
      } else {
        const index = this.breadcrumbs.findIndex((el: any) => el.label === this.document.name);
        if (index !== -1) {
          this.breadcrumbs[index].document = this.document;
        } else {
          this.breadcrumbs.push({
            iframeKey: crypto.randomBytes(16).toString("hex"),
            label: this.document.name,
            document: this.document,
            crossBreadcrumb: this.getCrossBeadcrumb(crossNavigationDocument, angularData),
          });
        }

        await this.loadPage();
      }
      this.documentMode = "VIEW";
    },
    checkIfParameterHasFixedValue(navigationParams: any, crossNavigationDocument: any) {
      if (!crossNavigationDocument || !crossNavigationDocument.navigationParams) return;
      Object.keys(crossNavigationDocument.navigationParams).forEach((key: string) => {
        const tempParam = crossNavigationDocument.navigationParams[key];
        if (tempParam.fixed) {
          navigationParams[key] = tempParam.value;
          navigationParams[key + "_field_visible_description"] = tempParam.value;
        }
      });
    },
    getCrossBeadcrumb(crossNavigationDocument: any, angularData: any) {
      let tempCrossBreadcrumb = crossNavigationDocument.crossBreadcrumb;
      if (tempCrossBreadcrumb?.includes("$P{")) {
        tempCrossBreadcrumb = this.updateCrossBreadCrumbWithParameterValues(tempCrossBreadcrumb, angularData);
      }

      return tempCrossBreadcrumb ?? this.document.name;
    },
    updateCrossBreadCrumbWithParameterValues(tempCrossBreadcrumb: string, angularData: any) {
      const parameterPlaceholders = tempCrossBreadcrumb.match(/{[\w\d]+}/g);
      if (!parameterPlaceholders) return "";
      const parameters = angularData.outputParameters;
      for (let i = 0; i < angularData.otherOutputParameters.length; i++) {
        const key = Object.keys(angularData.otherOutputParameters[i])[0];
        parameters[key] = angularData.otherOutputParameters[i][key];
      }
      const temp = [] as any[];
      for (let i = 0; i < parameterPlaceholders.length; i++) {
        const tempParameterName = parameterPlaceholders[i].substring(1, parameterPlaceholders[i].length - 1);
        temp.push({
          parameterPlaceholder: parameterPlaceholders[i],
          value: parameters[tempParameterName],
        });
      }
      let finalString = tempCrossBreadcrumb;
      for (let i = 0; i < temp.length; i++) {
        finalString = finalString.replaceAll("$P" + temp[i].parameterPlaceholder, temp[i].value);
      }

      return finalString;
    },
    addDocumentOtherParametersToNavigationParams(navigationParams: any[], angularData: any, crossNavigationDocument: any) {
      if (!angularData.outputParameters || angularData.outputParameters.length === 0 || !crossNavigationDocument?.navigationParams) return;
      const keys = Object.keys(angularData.outputParameters);
      const documentNavigationParamsKeys = Object.keys(crossNavigationDocument.navigationParams);
      for (let i = 0; i < keys.length; i++) {
        const tempKey = keys[i];
        let newKey = "";
        for (let j = 0; j < documentNavigationParamsKeys.length; j++) {
          if (crossNavigationDocument.navigationParams[documentNavigationParamsKeys[j]].value?.label === tempKey) {
            newKey = documentNavigationParamsKeys[j];
          }
        }
        if (newKey) navigationParams[newKey] = angularData.outputParameters[tempKey];
      }

      this.addSourceDocumentParameterValuesFromDocumentNavigationParameters(navigationParams, crossNavigationDocument);
    },
    addSourceDocumentParameterValuesFromDocumentNavigationParameters(navigationParams: any[], crossNavigationDocument: any) {
      const documentNavigationParamsKeys = Object.keys(crossNavigationDocument.navigationParams);
      documentNavigationParamsKeys.forEach((key: string) => {
        if (!navigationParams[key]) {
          const sourceParameter = this.filtersData.filterStatus.find((parameter: iParameter) => {
            return parameter.urlName === crossNavigationDocument.navigationParams[key].value.label;
          });
          if (sourceParameter) {
            navigationParams[key] = sourceParameter.parameterValue[0] && sourceParameter.parameterValue[0].value ? sourceParameter.parameterValue[0].value : "";
            navigationParams[key + "_field_visible_description"] = sourceParameter.parameterValue[0] && sourceParameter.parameterValue[0].description ? sourceParameter.parameterValue[0].description : "";
          }
        }
      });
    },
    openCrossNavigationInNewWindow(popupOptions: any, crossNavigationDocument: any, navigationParams: any) {
      if (!crossNavigationDocument || !crossNavigationDocument.document) return;
      const parameters = encodeURI(JSON.stringify(navigationParams));
      const url = process.env.VUE_APP_HOST_URL + `/knowage/restful-services/publish?PUBLISHER=documentExecutionNg&OBJECT_ID=${crossNavigationDocument.document.id}&OBJECT_LABEL=${crossNavigationDocument.document.label}&SELECTED_ROLE=${this.sessionRole}&SBI_EXECUTION_ID=null&OBJECT_NAME=${crossNavigationDocument.document.name}&CROSS_PARAMETER=${parameters}`;
      window.open(url, "_blank", `toolbar=0,status=0,menubar=0,width=${popupOptions.width || "800"},height=${popupOptions.height || "600"}`);
    },
    formatAngularOutputParameters(otherOutputParameters: any[]) {
      const startDocumentInputParameters = deepcopy(this.document.drivers);
      const keys = [] as any[];
      otherOutputParameters.forEach((parameter: any) => keys.push(Object.keys(parameter)[0]));
      for (let i = 0; i < startDocumentInputParameters.length; i++) {
        if (!keys.includes(startDocumentInputParameters[i].label)) {
          const tempObject = {} as any;
          tempObject[startDocumentInputParameters[i].label] = this.getParameterValueForCrossNavigation(startDocumentInputParameters[i].label);
          otherOutputParameters.push(tempObject);
        }
      }
    },
    getParameterValueForCrossNavigation(parameterLabel: string) {
      if (!parameterLabel) return;
      const index = this.filtersData.filterStatus?.findIndex((param: any) => param.label === parameterLabel);
      if (index !== -1 && this.filtersData.filterStatus[index].parameterValue[0]) {
        return this.filtersData.filterStatus[index].parameterValue[0].value;
      } else return "";
    },
    formatNavigationParams(otherOutputParameters: any[], navigationParams: any) {
      let formatedParams = {} as any;
      otherOutputParameters.forEach((el: any) => {
        let found = false;
        let label = "";

        for (let i = 0; i < Object.keys(navigationParams).length; i++) {
          if (navigationParams[Object.keys(navigationParams)[i]].value.label === Object.keys(el)[0]) {
            found = true;
            label = Object.keys(navigationParams)[i];
            break;
          }
        }

        if (found) {
          formatedParams[label] = el[Object.keys(el)[0]];
          formatedParams[label + "_field_visible_description"] = el[Object.keys(el)[0]];
        }
      });

      this.setNavigationParametersFromCurrentFilters(formatedParams, navigationParams);

      return formatedParams;
    },
    setNavigationParametersFromCurrentFilters(formatedParams: any, navigationParams: any) {
      const navigationParamsKeys = navigationParams ? Object.keys(navigationParams) : [];
      const formattedParameters = this.getFormattedParameters();
      const formattedParametersKeys = formattedParameters ? Object.keys(formattedParameters) : [];
      if (navigationParamsKeys.length > 0 && formattedParametersKeys.length > 0) {
        for (let i = 0; i < navigationParamsKeys.length; i++) {
          const index = formattedParametersKeys.findIndex((key: string) => key === navigationParams[navigationParamsKeys[i]].value.label && navigationParams[navigationParamsKeys[i]].value.isInput);
          if (index !== -1) {
            formatedParams[navigationParamsKeys[i]] = formattedParameters[formattedParametersKeys[index]];
            formatedParams[navigationParamsKeys[i] + "_field_visible_description"] = formattedParameters[formattedParametersKeys[index] + "_field_visible_description"] ? formattedParameters[formattedParametersKeys[index] + "_field_visible_description"] : "";
          }
        }
      }
    },
    showOLAPCustomView() {
      this.olapCustomViewVisible = true;
    },
    async executeOlapCustomView(payload: any) {
      this.loading = true;
      this.olapCustomViewVisible = false;
      await this.loadURL(payload);
      this.reloadTrigger = !this.reloadTrigger;
      this.loading = false;
    },
    async executeOLAPCrossNavigation(crossNavigationParams: any) {
      let temp = {} as any;
      this.loading = true;
      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/crossNavigation/${this.document.label}/loadCrossNavigationByDocument`).then((response: AxiosResponse<any>) => (temp = response.data));
      this.loading = false;

      if (!temp || temp.length === 0) {
        this.$store.commit("setError", {
          title: this.$t("common.error.generic"),
          msg: this.$t("documentExecution.main.crossNavigationNoTargetError"),
        });
        return;
      }

      this.document = {
        ...temp[0].document,
        navigationParams: this.formatOLAPNavigationParams(crossNavigationParams, temp[0].navigationParams),
      };

      const index = this.breadcrumbs.findIndex((el: any) => el.label === this.document.name);
      if (index !== -1) {
        this.breadcrumbs[index].document = this.document;
      } else {
        this.breadcrumbs.push({
          iframeKey: crypto.randomBytes(16).toString("hex"),
          label: this.document.name,
          document: this.document,
        });
      }

      await this.loadPage();
      this.reloadTrigger = !this.reloadTrigger;
    },
    formatOLAPNavigationParams(crossNavigationParams: any, navigationParams: any) {
      const crossNavigationParamKeys = Object.keys(crossNavigationParams);
      let formattedParams = {} as any;

      Object.keys(navigationParams).forEach((key: string) => {
        const index = crossNavigationParamKeys.findIndex((el: string) => el === navigationParams[key].value.label);
        if (index !== -1) {
          formattedParams[key] = crossNavigationParams[crossNavigationParamKeys[index]];
        }
      });

      return formattedParams;
    },
    isOlapDesignerMode() {
      if (this.$route.name === "olap-designer") {
        this.olapDesignerMode = true;
      }
    },
    isOrganizerEnabled() {
      return this.user.isSuperadmin || this.user.functionalities.includes("DocumentAdminManagement") || this.user.functionalities.includes("SaveIntoFolderFunctionality");
    },
    async addToWorkspace() {
      this.loading = true;
      await this.$http
        .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `2.0/organizer/documents/${this.document.id}`, {}, { headers: { "X-Disable-Errors": "true" } })
        .then(() => {
          this.$store.commit("setInfo", {
            title: this.$t("common.toast.updateTitle"),
            msg: this.$t("common.toast.success"),
          });
        })
        .catch((error) => {
          this.$store.commit("setError", {
            title: this.$t("common.toast.updateTitle"),
            msg: error.message === "sbi.workspace.organizer.document.addtoorganizer.error.duplicateentry" ? this.$t("documentExecution.main.addToWorkspaceError") : error.message,
          });
        });
      this.loading = false;
    },
    async loadUserConfig() {
      await this.$http.get(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/user-configs`).then((response: AxiosResponse<any>) => {
        if (response.data) {
          this.sessionEnabled = response.data["SPAGOBI.SESSION_PARAMETERS_MANAGER.enabled"] === "false" ? false : true;
          this.dateFormat = response.data["SPAGOBI.DATE-FORMAT-SERVER.format"] === "%Y-%m-%d" ? "dd/MM/yyyy" : response.data["SPAGOBI.DATE-FORMAT-SERVER.format"];
        }
      });
    },
    saveParametersInSession() {
      const tempFilters = deepcopy(this.filtersData);
      tempFilters.filterStatus?.forEach((filter: any) => {
        delete filter.dataDependsOnParameters;
        delete filter.dataDependentParameters;
        delete filter.dependsOnParameters;
        delete filter.dependentParameters;
        delete filter.lovDependsOnParameters;
        delete filter.lovDependentParameters;
      });

      sessionStorage.setItem(this.document.label, JSON.stringify(tempFilters));
    },
    async onCrossNavigationSelected(event: any) {
      this.destinationSelectDialogVisible = false;
      await this.loadCrossNavigation(event, this.angularData);
    },
    onCrossNavigationContainerClose() {
      this.crossNavigationContainerData = null;
      this.crossNavigationContainerVisible = true;
      this.crossNavigationSourceDocumentName = "";
      this.onBreadcrumbClick(this.breadcrumbs[this.breadcrumbs.length - 1]);
    },
    startReportAutoRefresh() {
      if (this.reportRefreshInterval) clearInterval(this.reportRefreshInterval);

      const refreshIntervalMs = this.document.refreshSeconds * 1000;
      this.reportRefreshInterval = setInterval(async () => {
        await this.refresh();
      }, refreshIntervalMs);
    },
  },
});
